import React, { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import "./select.css";

/**
 * Custom select (listbox) replacing the native <select>.
 * options: [{ value, label }]; onChange receives the selected value.
 */
const Select = ({ value, onChange, options, ariaLabel, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  );
  const selected = options[selectedIndex];

  const openList = () => {
    setActive(selectedIndex);
    setOpen(true);
  };

  const choose = (index) => {
    const opt = options[index];
    if (opt) onChange(opt.value);
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // Keep the active option visible
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active];
    el?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const onKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) openList();
        else setActive((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) openList();
        else setActive((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        if (open) { e.preventDefault(); setActive(0); }
        break;
      case "End":
        if (open) { e.preventDefault(); setActive(options.length - 1); }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (open) choose(active);
        else openList();
        break;
      case "Escape":
        if (open) { e.preventDefault(); e.stopPropagation(); setOpen(false); }
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        // Type-ahead: jump to first option starting with the typed letter
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          const idx = options.findIndex((o) =>
            String(o.label).toLowerCase().startsWith(e.key.toLowerCase())
          );
          if (idx >= 0) {
            if (!open) setOpen(true);
            setActive(idx);
          }
        }
    }
  };

  return (
    <div className={`select ${open ? "open" : ""} ${className}`} ref={rootRef}>
      <button
        type="button"
        className="select-trigger"
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
      >
        <span className={`select-value ${value ? "has-value" : ""}`}>
          {selected?.label}
        </span>
        <ChevronDown size={16} className="select-chevron" aria-hidden="true" />
      </button>

      {open && (
        <ul
          className="select-menu"
          role="listbox"
          id={listId}
          ref={listRef}
          aria-label={ariaLabel}
        >
          {options.map((opt, i) => (
            <li
              key={opt.value || "__all"}
              role="option"
              aria-selected={opt.value === value}
              className={`select-option ${i === active ? "active" : ""} ${
                opt.value === value ? "selected" : ""
              }`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(i)}
            >
              <span>{opt.label}</span>
              {opt.value === value && <Check size={15} aria-hidden="true" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Select;
